import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { formatApiError, setCurrentLang } from '@/lib/api';
import { getLang } from '@/lib/i18n';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Синхронизируем глобальную локаль для formatApiError (перевод бэкенд-ошибок).
  useEffect(() => {
    if (user && user.role !== 'organizer') {
      setCurrentLang(getLang(user.lang_code));
    } else {
      setCurrentLang('ru');
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', {
      email: (email || '').trim(),
      password: (password || '').trim(),
    });
    setUser(data);
    return data;
  };

  const register = async (formData) => {
    const payload = {
      ...formData,
      email: (formData.email || '').trim(),
      password: (formData.password || '').trim(),
      secret_code: (formData.secret_code || '').trim(),
    };
    const { data } = await api.post('/auth/register', payload);
    setUser(data);
    return data;
  };

  const setPassword = async (email, token, password) => {
    const { data } = await api.post('/auth/set-password', {
      email: (email || '').trim(),
      token: (token || '').trim(),
      password: (password || '').trim(),
    });
    setUser(data);
    return data;
  };

  const forgotPassword = async (email) => {
    const { data } = await api.post('/auth/forgot-password', {
      email: (email || '').trim(),
    });
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, setPassword, forgotPassword, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
